import { Controller, useForm } from "react-hook-form";
import { Input } from "@ui/input";
import TranslatedTextTemp from "@components/items/display/TranslatedTextTemp";
import { emailRegex } from "@utils/helpers/emailValidation";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import { useMemo, useState } from "react";
import { setMyEmailPassword, useAppDispatch } from "@lib/stores";
import { AuthActions } from "@lib/stores/auth/slices";
import { Eye, EyeOff } from "lucide-react";
import { translate } from "@modules/localizations/translate";

const NewEmailForm = () => {
  const [editState, setEditState] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showconfirmedPassword, setShowConfirmedPassword] =
    useState<boolean>(false);

  const dispatch = useAppDispatch();

  const buttonText = useMemo(() => {
    return editState ? "save" : "enter your email";
  }, [editState]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, dirtyFields },
  } = useForm<{ email: string; password: string; confirmedPassword: string }>({
    defaultValues: { email: "", password: "", confirmedPassword: "" },
    mode: "all",
  });

  const password = watch("password");

  const onSubmitForm = async (data: {
    email: string;
    password: string;
    confirmedPassword: string;
  }) => {
    try {
      const { email, password } = data;
      setMyEmailPassword({ email, password }, () => {
        dispatch(AuthActions.getMyInfoAsync());
      });
      setEditState(false);
      reset();
    } catch (error) {
      console.error("Error updating username:", error);
    }
  };

  return (
    <div className="col-span-2 grid grid-cols-2 items-center gap-2">
      {editState ? (
        <div className="flex flex-col gap-2 text-start">
          <Controller
            name="email"
            control={control}
            rules={{
              required: {
                value: true,
                message: translate(`settings.errors.${"required email"}`),
              },
              pattern: {
                value: emailRegex(),
                message: translate(`settings.errors.${"invalid email"}`),
              },
            }}
            render={({ field }) => (
              <div className="flex flex-col gap-2 text-start">
                <TranslatedTextTemp
                  text={`settings.titles.${"email"}`}
                  origintext={"email"}
                  className="text-[14px]"
                />
                <div className="flex flex-col">
                  <Input
                    {...field}
                    className="input-class"
                    type="email"
                    placeholder={translate(`settings.dialogs.${"enter email"}`)}
                  />
                  {errors.email && (
                    <p className="mt-1 self-start text-[12px] text-red-500">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>
            )}
          />
          <Controller
            name="password"
            control={control}
            rules={{
              required: {
                value: true,
                message: translate(`settings.errors.${"password is required"}`),
              },
              minLength: {
                value: 8,
                message: translate(
                  `settings.errors.${"the password must be at least 8 characters"}`
                ),
              },
            }}
            render={({ field }) => (
              <div className="flex flex-col gap-2 text-start">
                <TranslatedTextTemp
                  text={`settings.titles.${"password"}`}
                  origintext={"password"}
                  className="text-[14px]"
                />
                <div className="relative flex flex-col">
                  <Input
                    {...field}
                    className="input-class"
                    type={showPassword ? "text" : "password"}
                    placeholder={translate(
                      `settings.dialogs.${"enter new password"}`
                    )}
                  />
                  {dirtyFields.password && (
                    <div
                      className="absolute end-2 top-2 cursor-pointer text-gray-500"
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? <EyeOff /> : <Eye />}
                    </div>
                  )}
                  {errors.password && (
                    <p className="mt-1 self-start text-[12px] text-red-500">
                      {errors.password.message}
                    </p>
                  )}
                </div>
              </div>
            )}
          />
          <Controller
            name="confirmedPassword"
            control={control}
            rules={{
              required: {
                value: true,
                message: translate(`settings.errors.${"password is required"}`),
              },
              validate: (value) =>
                value === password ||
                translate(`settings.errors.${"passwords mismatch"}`),
            }}
            render={({ field }) => (
              <div className="flex flex-col gap-2 text-start">
                <TranslatedTextTemp
                  text={`settings.titles.${"confirmed password"}`}
                  origintext={"confirmed password"}
                  className="text-[14px]"
                />
                <div className="relative flex flex-col">
                  <Input
                    {...field}
                    className="input-class"
                    type={showconfirmedPassword ? "text" : "password"}
                    placeholder={translate(
                      `settings.dialogs.${"enter new password"}`
                    )}
                  />
                  {dirtyFields.confirmedPassword && (
                    <div
                      className="absolute end-2 top-2 cursor-pointer text-gray-500"
                      onClick={() => setShowConfirmedPassword((prev) => !prev)}
                    >
                      {showconfirmedPassword ? <EyeOff /> : <Eye />}
                    </div>
                  )}
                  {errors.confirmedPassword && (
                    <p className="mt-1 self-start text-[12px] text-red-500">
                      {errors.confirmedPassword.message}
                    </p>
                  )}
                </div>
              </div>
            )}
          />
        </div>
      ) : (
        <TranslatedTextTemp
          text={`settings.titles.${"not found"}`}
          origintext={"not found"}
        />
      )}
      <ShadcnButton
        className="bg-transparent hover:bg-transparent dark:bg-transparent dark:hover:bg-transparent"
        type="submit"
        action={
          editState ? handleSubmit(onSubmitForm) : () => setEditState(true)
        }
      >
        <TranslatedTextTemp
          text={`settings.titles.${buttonText}`}
          origintext={buttonText}
          className="dark:!text-dark-primary cursor-pointer text-[15px] font-medium !text-primary hover:opacity-70"
        />
      </ShadcnButton>
    </div>
  );
};

export default NewEmailForm;
